import { Record as LoftRecord, Vendor, POC, DistroList, FleetEntry, Report, ReportRunTarget, UserInfo, IpcSearchResponse } from './types';
import { getTokenFromStorage } from './auth';

const BASE = process.env.REACT_APP_API_URL || '';

/** Custom error class for API failures with status code and context. */
export class ApiError extends Error {
  public readonly statusCode: number;
  public readonly endpoint: string;

  constructor(statusCode: number, endpoint: string, message?: string) {
    super(message || `API request failed: ${endpoint} (${statusCode})`);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.endpoint = endpoint;
  }
}

/** Build authorization headers from the stored token. */
function authHeaders(): Record<string, string> {
  const token = getTokenFromStorage(); // hawkeye-suppress: rev-006 — runtime JWT read from storage for the Authorization header, not a secret
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function fetchJson<T>(url: string): Promise<T> {
  try {
    const res = await fetch(`${BASE}${url}`, { headers: authHeaders() });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new ApiError(res.status, url, `GET ${url} failed (${res.status}): ${body}`);
    }
    return res.json();
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw new ApiError(0, url, `Network error on GET ${url}: ${String(err)}`);
  }
}

async function postJson<T>(url: string, data: unknown): Promise<T> {
  try {
    const res = await fetch(`${BASE}${url}`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new ApiError(res.status, url, `POST ${url} failed (${res.status}): ${body}`);
    }
    return res.json();
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw new ApiError(0, url, `Network error on POST ${url}: ${String(err)}`);
  }
}

async function putJson<T>(url: string, data: unknown): Promise<T> {
  try {
    const res = await fetch(`${BASE}${url}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new ApiError(res.status, url, `PUT ${url} failed (${res.status}): ${body}`);
    }
    return res.json();
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw new ApiError(0, url, `Network error on PUT ${url}: ${String(err)}`);
  }
}

async function deleteReq(url: string): Promise<void> {
  try {
    const res = await fetch(`${BASE}${url}`, { method: 'DELETE', headers: authHeaders() });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new ApiError(res.status, url, `DELETE ${url} failed (${res.status}): ${body}`);
    }
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw new ApiError(0, url, `Network error on DELETE ${url}: ${String(err)}`);
  }
}

// ── Records ─────────────────────────────────────────
/** Fetch all maintenance records. */
export const getRecords = (): Promise<LoftRecord[]> => fetchJson<LoftRecord[]>('/api/records');
/** Create a new maintenance record. */
export const createRecord = (data: Partial<LoftRecord>): Promise<LoftRecord> => postJson<LoftRecord>('/api/records', data);
/** Update an existing maintenance record by ID. */
export const updateRecord = (id: number, data: Partial<LoftRecord>): Promise<LoftRecord> => putJson<LoftRecord>(`/api/records/${id}`, data);
/** Delete a maintenance record by ID. */
export const deleteRecord = (id: number): Promise<void> => deleteReq(`/api/records/${id}`);

// ── Vendors ─────────────────────────────────────────
/** Fetch all vendors. */
export const getVendors = (): Promise<Vendor[]> => fetchJson<Vendor[]>('/api/vendors');
/** Create a new vendor. */
export const createVendor = (data: Partial<Vendor>): Promise<Vendor> => postJson<Vendor>('/api/vendors', data);
/** Update an existing vendor by ID. */
export const updateVendor = (id: number, data: Partial<Vendor>): Promise<Vendor> => putJson<Vendor>(`/api/vendors/${id}`, data);
/** Delete a vendor by ID. */
export const deleteVendorApi = (id: number): Promise<void> => deleteReq(`/api/vendors/${id}`);

// ── POCs ────────────────────────────────────────────
/** Fetch all points of contact. */
export const getPocs = (): Promise<POC[]> => fetchJson<POC[]>('/api/pocs');
/** Create a new point of contact. */
export const createPoc = (data: Partial<POC>): Promise<POC> => postJson<POC>('/api/pocs', data);
/** Update an existing point of contact by ID. */
export const updatePoc = (id: number, data: Partial<POC>): Promise<POC> => putJson<POC>(`/api/pocs/${id}`, data);
/** Delete a point of contact by ID. */
export const deletePocApi = (id: number): Promise<void> => deleteReq(`/api/pocs/${id}`);

// ── Distro Lists ────────────────────────────────────
/** Fetch all distribution lists. */
export const getDistros = (): Promise<DistroList[]> => fetchJson<DistroList[]>('/api/distros');
/** Create a new distribution list. */
export const createDistro = (data: Partial<DistroList>): Promise<DistroList> => postJson<DistroList>('/api/distros', data);
/** Update an existing distribution list by ID. */
export const updateDistro = (id: number, data: Partial<DistroList>): Promise<DistroList> => putJson<DistroList>(`/api/distros/${id}`, data);
/** Delete a distribution list by ID. */
export const deleteDistroApi = (id: number): Promise<void> => deleteReq(`/api/distros/${id}`);

// ── Fleet ───────────────────────────────────────────
/** Fetch all fleet entries (carrier/tail/aircraft mappings). */
export const getFleet = (): Promise<FleetEntry[]> => fetchJson<FleetEntry[]>('/api/fleet');

// ── Reports ─────────────────────────────────────────
/** Fetch metadata for all generated monthly reports. */
export const getReports = (): Promise<Report[]> => fetchJson<Report[]>('/api/reports');
/** Get a short-lived presigned URL to download a report's PDF. */
export const getReportDownloadUrl = (id: string): Promise<{ url: string; expires_in: number }> =>
  fetchJson<{ url: string; expires_in: number }>(`/api/reports/download?id=${encodeURIComponent(id)}`);
/**
 * Trigger report generation now; resolves once generation completes. Pass a
 * list of targets to scope the run, or omit it to generate every report.
 */
export const runReports = (targets?: ReportRunTarget[]): Promise<{ message: string }> =>
  postJson<{ message: string }>('/api/reports/run', targets && targets.length ? { targets } : {});
/** Delete a report (metadata + stored PDF). */
export const deleteReportApi = (id: string): Promise<void> => deleteReq(`/api/reports?id=${encodeURIComponent(id)}`);
/**
 * Email a report (PDF attached) to its distribution list via SES. The From
 * address is a fixed application sender configured server-side; the requesting
 * user is set as Reply-To, so no per-user SES identity is required.
 */
export const sendReport = (id: string, to: string[], subject: string): Promise<{ sent: boolean; recipients: string[] }> =>
  postJson<{ sent: boolean; recipients: string[] }>('/api/reports/send', { id, to, subject });

// ── IPC lookup ──────────────────────────────────────
/**
 * Look up illustrated-parts-catalog matches for an equipment part number. The
 * carrier selects which aircraft knowledge base is searched; results include
 * presigned links to the source IPC PDFs (with a #page anchor when known).
 */
export const searchIpc = (carrier: string, partNumber: string): Promise<IpcSearchResponse> =>
  fetchJson<IpcSearchResponse>(
    `/api/ipc-search?carrier=${encodeURIComponent(carrier)}&part_number=${encodeURIComponent(partNumber)}`,
  );

// ── Auth ────────────────────────────────────────────
/** Fetch the current authenticated user's info and role. */
export const getMe = (): Promise<UserInfo> => fetchJson<UserInfo>('/api/me');
